import { useContext } from "react";
import { Routes, Route, Outlet, Navigate } from "react-router-dom";

import "./index.css";
import "./chart.css";
import "./step.css";

import Nav from "./layouts/nav";
import Footer from "./layouts/Footer";
import ScrollToTop from "./layouts/ScrollToTop";

import Error403 from "./pages/Error403";
import Error404 from "./pages/Error404";
import { ThemeContext } from "../context/ThemeContext";

import AdminDashboard from "./components/Vault/AdminDashboard";
import UserWalletBalances from "./components/Vault/UserWalletBalances";
import MyWalletDashboard from "./components/Vault/MyWalletDashboard";
import PartnerExpenseTagStats from "./components/Vault/PartnerExpenseTagStats";
import MyExpenseTagStats from "./components/Vault/MyExpenseTagStats";
import PartnerDetails from "./components/PartnerDetails";
import SpecialRequestApprovalsMainTable from "./components/PartnerReceipts/MyRequests";
import PartnerLedger from "./components/PartnerLedger"
import MyActivity from "./components/MyActivity";
import Teams from "./components/Teams";
import MyReceiptsMainTable from "./components/MyReceiptsMainTable";
import ManagePartners from "./components/ManagePartners";
import ManageExecAdmins from "./components/ManageExecAdmins";
import UserProfile from "./components/UserProfile";
import Faq from "./components/Faq";
import SetTransactionPin from "./pages/SetTransactionPin";
import ResetTransactionPinLanding from "./pages/ResetTransactionPinLanding";
import TeamDetails from "./components/TeamDetails";
import ReceiptReviewsMainTable from "./components/ReceiptReviewsMainTable";
import Approvals from "./components/PartnerReceipts/Approvals"
import FloatHealth from "./components/FloatHealth";
import FundTransfers from "./components/FundTransfers";
import SuspendedUsers from "./components/SuspendedUsers";
import CapitalInjections from "./components/CapitalInjections";
import ReversalsPage from "./components/ReversalsPage";
import FeeRates from "./components/FeeRates";
import ChartOfAccounts from "./components/ChartOfAccounts";
import SignInActivity from "./components/SignInActivity";
import Users from "./components/Users";
import RolesAndPermissions from "./components/RolesAndPermissions";
import Reconciliation from "./components/Reconciliation";
import PermissionGate from "./components/PermissionGate";
import TransactionPinExpiryBanner from "./components/TransactionPinExpiryBanner";
import { SecurityModalsProvider } from "../context/SecurityModalsContext";
import { ROLE_SLUGS, PERMISSION_SLUGS } from "../utils/auth/permissionConstants";

const Markup = () => {
  const privateRoutes = [
    { url: "vault-dashboard",                            component: <AdminDashboard/>,                      requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_DASHBOARD,  requiresPartner: true },
    { url: "vault-dashboard/user-wallet-balances",       component: <UserWalletBalances/>,                  requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_DASHBOARD,  requiresPartner: true },
    { url: "my-balance-dashboard",                       component: <MyWalletDashboard/>,                   requiresUserWallet: true,                                          requiresPartner: true },
    { url: "ledger",                                     component: <PartnerLedger/>,                       requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_STATEMENTS,  requiresPartner: true },
    { url: "my-activity",                                component: <MyActivity/>,                          requiresUserWallet: true,                                          requiresPartner: true },
    { url: "teams",                                      component: <Teams/>,                               requiredPermissionSlug: PERMISSION_SLUGS.MANAGE_TEAMS,             requiresPartner: true },
    { url: "my-receipts",                                component: <MyReceiptsMainTable/>,                 requiresUserWallet: true,                                          requiresPartner: true },
    { url: "manage-receipts",                            component: <ReceiptReviewsMainTable/>,             requiredPermissionSlug: PERMISSION_SLUGS.MANAGE_RECEIPTS,          requiresPartner: true },
    { url: "manage-partners",                            component: <ManagePartners/>,                      requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN },
    { url: "manage-exec-admins",                         component: <ManageExecAdmins/>,                    requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN },
    { url: "user-profile",                               component: <UserProfile/> },
    { url: "faq",                                        component: <Faq/> },
    { url: "sign-in-activity",                           component: <SignInActivity/> },
    { url: "users",                                      component: <Users/>,                               requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_DETAILS,     requiresPartner: true },
    { url: "set-transaction-pin",                        component: <SetTransactionPin/> },
    { url: "reset-transaction-pin",                      component: <ResetTransactionPinLanding/> },
    { url: "admin-details",                              component: <PartnerDetails/>,                      requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_DETAILS,     requiresPartner: true },
    { url: "suspended-users",                            component: <SuspendedUsers/>,                      requiredPermissionSlug: PERMISSION_SLUGS.MANAGE_PARTNER_USERS,     requiresPartner: true },
    { url: "admin-details/:id",                          component: <PartnerDetails/>,                      requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_DETAILS,     requiresPartner: true },
    { url: "team-details/:teamId/partners/:partnerId",   component: <TeamDetails/>,                         requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_DETAILS,     requiresPartner: true },
    { url: "my-requests",                                component: <SpecialRequestApprovalsMainTable/>,    requiresUserWallet: true,                                          requiresPartner: true },
    { url: "approvals",                                  component: <Approvals/>,                           requiredPermissionSlug: PERMISSION_SLUGS.CAN_REVIEW_APPROVALS,     requiresPartner: true },
    { url: "fund-transfers",                             component: <FundTransfers/>,                       requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN },
    { url: "health",                                     component: <FloatHealth/>,                         requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN },
    { url: "capital-injections",                         component: <CapitalInjections/>,                   requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN },
    { url: "reversals",                                  component: <ReversalsPage/>,                       requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN },
    { url: "fee-rates",                                  component: <FeeRates/>,                            requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN },
    { url: "chart-of-accounts",                          component: <ChartOfAccounts/>,                     requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN },
    { url: "roles-and-permissions",                      component: <RolesAndPermissions/>,                 requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN },
    { url: "reconciliation",                             component: <Reconciliation/>,                      requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN },
    { url: "top-up-reconciliation",                      component: <Navigate to="/reconciliation?tab=top-ups" replace />, requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN },
    { url: "expense-tag-stats",                          component: <PartnerExpenseTagStats/>,              requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_DETAILS,     requiresPartner: true },
    { url: "my-expense-tag-stats",                       component: <MyExpenseTagStats/>,                   requiresUserWallet: true,                                          requiresPartner: true },
  ];

  return (
    <>
      <Routes>
        <Route path="page-error-403" element={<Error403/>}/>
        <Route path="page-error-404" element={<Error404/>}/>
        <Route path="/*" element={<MainLayout/>}>
          {privateRoutes.map((data, i) => (
            <Route
              key={i}
              path={`${data.url}`}
              element={
                <PermissionGate
                  requiredRoleSlug={data.requiredRoleSlug}
                  requiredPermissionSlug={data.requiredPermissionSlug}
                  requiresUserWallet={data.requiresUserWallet}
                  requiresPartnerVault={data.requiresPartnerVault}
                  requiresPartner={data.requiresPartner}
                  requiresTransactionPin={data.requiresTransactionPin}
                >
                  {data.component}
                </PermissionGate>
              }
            />
          ))}
          <Route path="*" element={<Error404/>} />
        </Route>
      </Routes>
      <ScrollToTop/>

    </>
  );
};

function MainLayout () {
  const { menuToggle } = useContext(ThemeContext);
  return (
    <SecurityModalsProvider>
      <div id="main-wrapper" className={`show ${menuToggle ? "menu-toggle" : ""}`}>
        <Nav/>
        <div className="content-body">
          <div className="container-fluid">
            <TransactionPinExpiryBanner/>
            <Outlet/>
          </div>
        </div>
        <Footer/>
      </div>
    </SecurityModalsProvider>
  );
};

export default Markup;
